import { useState } from "react";
import { QuizResponse } from "../../api/Quiz/SubmitQuiz";
import CheckSignIcon from "../Icons/CheckSignIcon";
import WrongSignIcon from "../Icons/WrongSignIcon";
import Navbar from "../Layout/Navbar";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import rehypeRaw from 'rehype-raw';

const PostMcqExam = ({ submissionResponse }: { submissionResponse?: QuizResponse }) => {
  const [currentTab, setCurrentTab] = useState("wrong");
  const { id } = useParams();
  const quizId = id || "";
  const navigate = useNavigate();

  const handleRetakeExam = () => {
    navigate(`/exam/${quizId}`, { replace: true });
    window.location.reload();
  };

  return (
    <>
      <Navbar />
      <div id="table" className="bg-white w-full min-h-screen h-full py-28">
        <div className="container">
          <div className="sm:w-[472px] py-[18px] px-6">
            <div className="flex gap-8 justify-between font-bold md:text-2xl text-lg">
              <div>
                <p className="mb-2.5 sm:mb-5">Percentage</p>
                <p>Status</p>
              </div>
              <div>
                <p className="text-primary mb-2.5 sm:mb-5">{submissionResponse?.score}%</p>
                <p className={`text-${submissionResponse?.status === 'passed' ? 'green' : 'red'}-500`}>{submissionResponse?.status}</p>
              </div>
            </div>
          </div>
          <div className="md:text-2xl sm:text-xl text-base max-w-3xl text-center mx-auto mb-4">
            {submissionResponse?.status === 'passed' ? (
              <h1>
                Congratulations you passed the first step of the exam, you can start the second step by clicking here. <Link className="font-bold text-primary underline" to={`/exam/${quizId}/part-2`}>Essay Questions</Link>
              </h1>
            ) : (
              <h1>
                Sorry, you couldn't pass the MCQ with more than 50%. You need to retake the exam and pass this step to proceed to the essay questions. <button className="font-bold text-primary underline" onClick={handleRetakeExam}>Retake Exam</button>
              </h1>
            )}
          </div>
          <div className="w-full bg-[#F2F4F7] md:py-2.5 py-1.5 flex gap-4 rounded-lg border-[1.5px] border-black/5 px-2.5 mt-1">
            <button className={`transition-all duration-300 h-12 md:h-[70px] w-full rounded-md font-medium text-lg md:text-xl ${currentTab === 'wrong' ? 'bg-white shadow border border-black/15 text-primary' : ''}`} onClick={() => setCurrentTab('wrong')}>Wrong</button>
            <button className={`transition-all duration-300 h-12 md:h-[70px] w-full rounded-md font-medium text-lg md:text-xl ${currentTab === 'correct' ? 'bg-white shadow border border-black/15 text-primary' : ''}`} onClick={() => setCurrentTab('correct')}>Correct</button>
            <button
              className={`transition-all duration-300 h-12 md:h-[70px] w-full rounded-md font-medium text-lg md:text-xl ${currentTab === "marked"
                  ? "bg-white shadow border border-black/15 text-primary"
                  : ""
                }`}
              onClick={() => setCurrentTab("marked")}
            >
              Marked
            </button>
          </div>
          <div className="mt-8 md:space-y-28 space-y-12">
            {currentTab === "wrong" && submissionResponse?.wrongQuestions?.map((question, index) => (
              <div key={index}>
                <h1 className="md:mb-10 mb-5 font-bold text-lg md:text-2xl">
                  <ReactMarkdown className="text-justify" rehypePlugins={[rehypeRaw]}>
                        {question.question}
                  </ReactMarkdown></h1>
                <div className="md:space-y-12 space-y-6 font-bold sm:text-base text-sm md:text-2xl w-full">
                  {question.choices.map((choice, choiceIndex) => (
                    <div className="w-full" key={choiceIndex}>
                      {choice.selected && !choice.isCorrect &&
                        <h1 className="border-[#D80B0B] border rounded-xl items-center gap-2 bg-[#FFF0F0] text-[#D80B0B] flex w-fit sm:py-3 py-1.5 sm:px-4 px-3">{choice.answer}  <div className="min-w-4 min-h-4"><WrongSignIcon /></div> </h1>
                      }
                      {!choice.selected && choice.isCorrect &&
                        <h1 className="border-[#22BB64] border rounded-xl items-center gap-2 bg-[#EFFEF4] text-[#22BB64] flex w-fit sm:py-3 py-1.5 sm:px-4 px-3">{choice.answer} <div className="min-w-4 min-h-4"><CheckSignIcon /></div> </h1>
                      }
                      {!choice.selected && !choice.isCorrect &&
                        <h1 className="border border-gray-300 rounded-xl items-center gap-2 bg-white text-gray-500 flex w-fit sm:py-3 py-1.5 sm:px-4 px-3">{choice.answer}</h1>
                      }
                    </div>
                  ))}
                </div>
                <h1 className="mt-10 font-semibold sm:text-base text-sm">
                    Explanation:{" "}
                    <span className="text-primary">
                      <ReactMarkdown className="text-justify" rehypePlugins={[rehypeRaw]}>
                        {question.explanation}
                      </ReactMarkdown>
                    </span>
                  </h1>              </div>
            ))}
            {currentTab === "correct" && submissionResponse?.correctQuestions?.map((question, index) => (
              <div key={index}>
                <h1 className="md:mb-10 mb-5 font-bold text-lg md:text-2xl">
                  <ReactMarkdown className="text-justify" rehypePlugins={[rehypeRaw]}>
                        {question.question}
                  </ReactMarkdown></h1>
                <div className="md:space-y-12 space-y-6 font-bold text-base md:text-2xl">
                  {question.choices.map((choice, choiceIndex) => (
                    <div key={choiceIndex}>
                      {choice.selected && choice.isCorrect &&
                        <h1 className="border-[#22BB64] border rounded-xl items-center gap-2 bg-[#EFFEF4] text-[#22BB64] flex w-fit sm:py-3 py-1.5 sm:px-4 px-3">{choice.answer}  <div className="min-w-4 min-h-4"><CheckSignIcon /></div> </h1>
                      }
                      {!choice.selected && !choice.isCorrect &&
                        <h1 className="border border-gray-300 rounded-xl items-center gap-2 bg-white text-gray-500 flex w-fit sm:py-3 py-1.5 sm:px-4 px-3">{choice.answer}</h1>
                      }
                    </div>
                  ))}
                </div>
                <h1 className="mt-10 font-semibold sm:text-base text-sm">
                    Explanation:{" "}
                    <span className="text-primary">
                      <ReactMarkdown className="text-justify" rehypePlugins={[rehypeRaw]}>
                        {question.explanation}
                      </ReactMarkdown>
                    </span>
                  </h1>              </div>
            ))}
                        {currentTab === "marked" &&
              submissionResponse?.markedQuestions?.map((question, index) => (
                <div key={index}>
                  <h1 className="md:mb-10 mb-5 font-bold text-lg md:text-2xl">
                    <ReactMarkdown className="text-justify" rehypePlugins={[rehypeRaw]}>
                        {question.question}
                    </ReactMarkdown>
                  </h1>
                  <div className="md:space-y-12 space-y-6 font-bold text-base md:text-2xl">
                    {question.choices.map((choice, choiceIndex) => (
                      <div key={choiceIndex}>
                        {choice.selected && !choice.isCorrect && (
                          <h1 className="border-[#D80B0B] border rounded-xl items-center gap-2 bg-[#FFF0F0] text-[#D80B0B] flex w-fit sm:py-3 py-1.5 sm:px-4 px-3">
                            {choice.answer}{" "}
                            <div className="min-w-4 min-h-4">
                              <WrongSignIcon />
                            </div>{" "}
                          </h1>
                        )}
                        {choice.selected && choice.isCorrect && (
                          <h1 className="border-[#22BB64] border rounded-xl items-center gap-2 bg-[#EFFEF4] text-[#22BB64] flex w-fit sm:py-3 py-1.5 sm:px-4 px-3">
                            {choice.answer}{" "}
                            <div className="min-w-4 min-h-4">
                              <CheckSignIcon />
                            </div>{" "}
                          </h1>
                        )}
                        {!choice.selected && choice.isCorrect && (
                          <h1 className="border-[#22BB64] border rounded-xl items-center gap-2 bg-[#EFFEF4] text-[#22BB64] flex w-fit sm:py-3 py-1.5 sm:px-4 px-3">
                            {choice.answer}{" "}
                            <div className="min-w-4 min-h-4">
                              <CheckSignIcon />
                            </div>{" "}
                          </h1>
                        )}
                        {!choice.selected && !choice.isCorrect && (
                          <h1 className="border border-gray-300 rounded-xl items-center gap-2 bg-white text-gray-500 flex w-fit sm:py-3 py-1.5 sm:px-4 px-3">
                            {choice.answer}
                          </h1>
                        )}
                      </div>
                    ))}
                  </div>
                  <h1 className="mt-10 font-semibold sm:text-base text-sm">
                    Explanation:{" "}
                    <span className="text-primary">
                      <ReactMarkdown className="text-justify" rehypePlugins={[rehypeRaw]}>
                        {question.explanation}
                      </ReactMarkdown>
                    </span>
                  </h1>
                </div>
              ))}

          </div>
        </div>
      </div>
    </>
  );
};

export default PostMcqExam;
